import React from "react";
import {
  cacheBustingReload,
  hasAlreadyReloadedForChunkError,
  isModuleLoadError,
} from "@/lib/lazyWithRetry";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  label?: string;
  silent?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const tag = this.props.label ? `[ErrorBoundary:${this.props.label}]` : "[ErrorBoundary]";
    console.error(`${tag} Caught error:`, error);
    console.error(`${tag} Error name:`, error.name);
    console.error(`${tag} Error message:`, error.message);
    console.error(`${tag} Error stack:`, error.stack);
    console.error(`${tag} Component stack:`, errorInfo.componentStack);
    
    // Try to identify which component failed
    if (errorInfo.componentStack) {
      const componentMatch = errorInfo.componentStack.match(/at (\w+)/);
      if (componentMatch) {
        console.error(`${tag} Failed component:`, componentMatch[1]);
      }
    }

    // A missing/stale app file: self-heal once with a cache-busting reload.
    if (isModuleLoadError(error) && !hasAlreadyReloadedForChunkError()) {
      cacheBustingReload();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined });
    // Cache-busting reload so a stale/failed asset cannot be served again
    cacheBustingReload(true);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.silent) {
        return this.props.fallback ?? null;
      }
      const chunkError = isModuleLoadError(this.state.error);
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
          {this.props.fallback ?? (
            <div className="max-w-lg w-full border border-border rounded-lg p-6 shadow-lg bg-card">
              <h1 className="text-xl font-semibold">
                {chunkError ? "We couldn't load part of the app" : "Something went wrong"}
              </h1>
              <p className="mt-2 text-muted-foreground">
                {chunkError
                  ? "A file needed for this page didn't download properly. Reloading with a fresh copy usually fixes it."
                  : "An unexpected error occurred. Please try reloading the page."}
              </p>
              {this.state.error && (
                <div className="mt-4 p-4 bg-muted rounded-md">
                  <p className="text-xs font-mono text-muted-foreground mb-2">Error Details:</p>
                  <pre className="text-xs overflow-auto whitespace-pre-wrap text-muted-foreground/80 max-h-40">
                    {this.state.error.name}: {this.state.error.message}
                    {'\n\n'}
                    {this.state.error.stack}
                  </pre>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Check the browser console for full details including component stack.
                  </p>
                </div>
              )}
              <button
                onClick={this.reset}
                className="mt-4 inline-flex items-center justify-center rounded-md px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition"
              >
                Reload
              </button>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
